import React, { useEffect, useState } from "react";
import { Box, Paper, Grid, TextField, MenuItem, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useMasterData } from "../../../hooks/useMasterData";
import { certificateApi } from "./certificateApi";
import CertificateForm from "./CertificateForm";
import CertificatePreview from "./CertificatePreview";
import CertificateHistoryTable from "./CertificateHistoryTable";

const CertificatesPage: React.FC = () => {
  const { masters, fetchMasters, fetchAcademicSessions, fetchSections } = useMasterData();
  const [templates, setTemplates] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [filters, setFilters] = useState<{ sessionId?: string; classId?: string; sectionId?: string; search?: string }>({});
  const [sections, setSections] = useState<{ id: string; name: string }[]>([]);
  const [students, setStudents] = useState<Array<any>>([]);
  const [form, setForm] = useState({ studentId: "", templateId: "", issueDate: new Date().toISOString().split("T")[0], remarks: "", extraFields: {} as Record<string, string> });
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [history, setHistory] = useState<Array<any>>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [requiredFields, setRequiredFields] = useState<Array<{ key: string; label: string; type: string; options?: string[] }>>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({ open: false, message: "", severity: "info" });

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
    certificateApi.templates().then(setTemplates);
  }, []);

  useEffect(() => {
    if (filters.classId) {
      fetchSections(filters.classId).then(setSections);
    } else {
      setSections([]);
      setFilters((p) => ({ ...p, sectionId: "" }));
    }
  }, [filters.classId]);

  useEffect(() => {
    const loadFields = async () => {
      if (!form.templateId) {
        setRequiredFields([]);
        setForm((p) => ({ ...p, extraFields: {} }));
        return;
      }
      try {
        const meta = await certificateApi.templateFields(form.templateId);
        setRequiredFields(meta.requiredFields || []);
        const initial: Record<string, string> = {};
        (meta.requiredFields || []).forEach((f) => {
          initial[f.key] = "";
        });
        setForm((p) => ({ ...p, extraFields: initial }));
      } catch (e: any) {
        setRequiredFields([]);
        setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load template fields", severity: "error" });
      }
    };
    loadFields();
  }, [form.templateId]);

  const loadStudents = async () => {
    try {
      const res = await certificateApi.students(filters);
      setStudents(res);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load students", severity: "error" });
    }
  };

  const loadHistory = async () => {
    if (!form.studentId) {
      setSnackbar({ open: true, message: "Please select a student first", severity: "error" });
      return;
    }
    try {
      const res = await certificateApi.history({ studentId: form.studentId, limit: 50 });
      setHistory(res.items);
      setHistoryOpen(true);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load history", severity: "error" });
    }
  };

  const preview = async () => {
    try {
      const res = await certificateApi.generate({ ...form, preview: "true" });
      setPreviewHtml(res.content);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to preview", severity: "error" });
    }
  };

  const generate = async () => {
    try {
      const res = await certificateApi.generate(form);
      setSnackbar({ open: true, message: "Certificate generated", severity: "success" });
      setPreviewHtml("");
      loadHistory();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to generate", severity: "error" });
    }
  };

  const downloadPdf = async (id: string, number: string) => {
    try {
      const res = await certificateApi.pdf(id);
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      let filename = `certificate-${number}.pdf`;
      const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
      if (cd) {
        const m = cd.match(/filename=\"?([^\";]+)\"?/i);
        if (m && m[1]) filename = m[1];
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to download PDF", severity: "error" });
    }
  };

  const resetForm = () => {
    setForm({ studentId: "", templateId: "", issueDate: new Date().toISOString().split("T")[0], remarks: "", extraFields: {} });
    setPreviewHtml("");
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ fontSize: 22, fontWeight: 600, mb: 2 }}>Issue Certificate</Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Session"
              value={filters.sessionId || ""}
              onChange={(e) => setFilters({ ...filters, sessionId: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {masters.academicSessions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Class" value={filters.classId || ""} onChange={(e) => setFilters({ ...filters, classId: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              {masters.classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Section"
              value={filters.sectionId || ""}
              disabled={!filters.classId}
              onChange={(e) => setFilters({ ...filters, sectionId: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {sections.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Search" fullWidth value={filters.search || ""} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" onClick={loadStudents}>
              Load Students
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <CertificateForm
          students={students}
          templates={templates}
          requiredFields={requiredFields}
          form={form}
          onChange={setForm}
          onPreview={preview}
          onGenerate={generate}
          onReset={resetForm}
          onHistory={loadHistory}
        />
      </Paper>

      {previewHtml && (
        <Box sx={{ mb: 2 }}>
          <CertificatePreview html={previewHtml} certificateType={
              templates.find(t => t.id === form.templateId)?.code
          } />
        </Box>
      )}

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Certificate History</DialogTitle>
        <DialogContent dividers>
          <CertificateHistoryTable rows={history} onDownload={downloadPdf} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CertificatesPage;
